import React, { useState } from "react";
import { Modal, Button, Typography, Space, message, Tooltip } from "antd";
import {
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import ScormPreviewTest2 from "./legacyComp/tetsing/ScormPreviewTest2";
import { SCORM_PREVIEW } from "../config";

const { Title, Text } = Typography;

const FilePreviewModal = ({
  open,
  onClose,
  fileUrl,
  selectedRecord,
  showName = true,
}) => {
  const [zoomPercent, setZoomPercent] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isReadyToRender, setIsReadyToRender] = useState(false);
  // console.log(selectedRecord);
  const fileName = fileUrl?.split("/")?.pop() || "Unknown File";
  const fileType = fileName?.split(".")?.pop()?.toUpperCase() || "N/A";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
    fileType.toLowerCase()
  );

  const handleDownload = () => {
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      message.error("Failed to download file.");
    }
  };

  const docs = [
    {
      uri: fileUrl,
      fileType: fileType.toLowerCase(),
      fileName,
    },
  ];

  const docViewerKey = `${fileUrl}-${zoomPercent}-${rotation}`;

  return (
    <Modal
      open={open}
      onCancel={() => {
        setZoomPercent(100);
        setRotation(0);
        setHasError(false);
        setIsReadyToRender(false);
        onClose();
      }}
      afterOpenChange={(open) => {
        if (open) {
          setTimeout(() => setIsReadyToRender(true), 100);
        }
      }}
      footer={null}
      width="60%"
      centered
      bodyStyle={{ padding: "16px 24px", height: "80vh" }}
    >
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {showName && selectedRecord?.name ? `${selectedRecord.name} | ` : ""}
          {fileName}
        </Title>
        <Text type="secondary">Type: {fileType}</Text>
      </div>

      {/* Viewer */}
      {fileType === "XML" || selectedRecord?.type === "SCORM" ? (
        <ScormPreviewTest2
          link={`${SCORM_PREVIEW}${fileUrl.replace(
            /imsmanifest(\.xml)?$/i,
            ""
          )}`}
        />
      ) : (
        <>
          <div
            style={{
              height: "calc(100% - 120px)",
              border: "1px solid rgb(24, 22, 22)",
              padding: 10,
              display: isImage ? "flex" : "block",
              justifyContent: isImage ? "center" : undefined,
              alignItems: isImage ? "center" : undefined,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                overflow: "auto",
                width: "100%",
                height: "100%",
              }}
            >
              {!hasError && fileUrl && isReadyToRender ? (
                <div
                  style={{
                    width: `${zoomPercent}%`,
                    minWidth: "300px",
                    height: "100%", // make it fill the height
                    margin: isImage ? "auto" : undefined,
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: "center",
                    transition: "transform 0.3s ease",
                  }}
                >
                  <DocViewer
                    key={docViewerKey}
                    documents={docs}
                    pluginRenderers={DocViewerRenderers}
                    style={{ width: "100%", height: "auto" }}
                    config={{
                      header: {
                        disableHeader: true,
                        disableFileName: true,
                      },
                    }}
                    onDocumentLoadFailed={() => {
                      setHasError(true);
                      message.error("Failed to load the document.");
                    }}
                  />
                </div>
              ) : (
                <Text type="secondary">Loading preview...</Text>
              )}
            </div>
          </div>
          {/* Footer controls */}
          <div
            style={{
              marginTop: 16,
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space size="middle">
              <Tooltip title="Zoom Out">
                <Button
                  shape="circle"
                  icon={<ZoomOutOutlined />}
                  onClick={() => setZoomPercent((z) => Math.max(z - 10, 50))}
                />
              </Tooltip>
              <Tooltip title="Zoom In">
                <Button
                  shape="circle"
                  icon={<ZoomInOutlined />}
                  onClick={() => setZoomPercent((z) => Math.min(z + 10, 200))}
                />
              </Tooltip>
              <Tooltip title="Reset Zoom">
                <Button
                  shape="circle"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setZoomPercent(100);
                    setRotation(0);
                  }}
                />
              </Tooltip>
              <Tooltip title="Rotate 90°">
                <Button
                  shape="circle"
                  icon={<RotateRightOutlined />}
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                />
              </Tooltip>
              <Text type="secondary">Zoom: {zoomPercent}%</Text>
            </Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default FilePreviewModal;
